import type {
  AnySort,
  DependencyUsage,
  Grouping,
  LicensingFilter,
  LicensingIdentification,
  QualityFilter,
  VulnFilter,
  VulnUpgradeDistance,
} from '../issuesV2';
import {
  Category,
  CREATED_AT_SORT,
  EPSS_SORT,
  ISSUE_COUNT_SORT,
  LICENSING_ISSUE_TYPE,
  VulnSeverity,
} from '../issuesV2';

export type FilterForCategory<C extends Category = Category> = {
  [Category.Licensing]: LicensingFilter;
  [Category.Vulnerability]: VulnFilter;
  [Category.Quality]: QualityFilter;
}[C];

export type NamedFilter<C extends Category = Category> = {
  id: number;
  name: string;
  category: C;
  filter: FilterForCategory<C>;
};

export type SavedFilter<C extends Category = Category> = NamedFilter<C> & {
  sort: AnySort;
  group: Grouping;
};

export type ProjectIssueFilter = {
  id: number;
  projectId: string;
  category: Category;
  filterId: number;

  createdAt: Date;
  updatedAt: Date;
};

export const FILTER_NAME_SIZE_LIMIT = 80;

/**
 * These are the filters that are created by default for every organization.
 */
export const DEFAULT_SAVED_FILTERS: Array<Pick<SavedFilter, 'category' | 'name' | 'filter' | 'sort' | 'group'>> = [
  {
    category: Category.Vulnerability,
    name: 'Lowest Effort to Fix',
    filter: {
      depths: ['direct'] as DependencyUsage[],
      upgradeDistance: ['patch', 'minor'] as VulnUpgradeDistance[],
    },
    group: 'revision',
    sort: ISSUE_COUNT_SORT.DESC,
  },
  {
    category: Category.Vulnerability,
    name: 'Critical CVEs with Fix Available',
    filter: {
      severity: [VulnSeverity.Critical, VulnSeverity.High] as VulnSeverity[],
      upgradeDistance: ['patch', 'minor', 'major', 'unknown'] as VulnUpgradeDistance[],
    },
    group: 'issue',
    sort: EPSS_SORT.DESC,
  },
  {
    category: Category.Vulnerability,
    name: 'Recently Found with Fix Available',
    filter: {
      upgradeDistance: ['patch', 'minor', 'major', 'unknown'] as VulnUpgradeDistance[],
      foundAfter: 'last30days',
    },
    group: 'issue',
    sort: CREATED_AT_SORT.DESC,
  },
  {
    category: Category.Licensing,
    name: 'Critical Licensing Issues',
    filter: {
      type: [LICENSING_ISSUE_TYPE.PolicyConflict],
      depths: ['direct'] as DependencyUsage[],
    },
    group: 'revision',
    sort: ISSUE_COUNT_SORT.DESC,
  },
  {
    category: Category.Licensing,
    name: 'Discovered Licensing Issues',
    filter: {
      type: [LICENSING_ISSUE_TYPE.PolicyConflict],
      identification: ['discovered'] as LicensingIdentification[],
    },
    group: 'revision',
    sort: ISSUE_COUNT_SORT.DESC,
  },
];
